/**
 * 🚀 HERO HEADER CONTAINER HELPERS 2025
 * 
 * Helper Functions für das Dynamic Container Sections System
 * - Migration von alten Widgets
 * - Container Management (Add, Delete, Reorder)
 * - Grid Layout Logic
 */

/**
 * 🔥 Helper: Enriches element with default styling properties if missing
 * Firebase deletes undefined properties, so we need defaults!
 */
const enrichElementWithDefaults = (element) => {
    // Wenn Element keine Styling-Properties hat, füge Defaults hinzu basierend auf elementType
    const getDefaultsByType = (elType) => {
        const defaults = {
            'h1': { fontSize: 48, fontSizeMobile: 36, fontWeight: '700', fontWeightMobile: '700', lineHeight: 1.2, lineHeightMobile: 1.2, maxWidth: 800, maxWidthMobile: 700 },
            'h2': { fontSize: 36, fontSizeMobile: 28, fontWeight: '600', fontWeightMobile: '600', lineHeight: 1.3, lineHeightMobile: 1.3, maxWidth: 700, maxWidthMobile: 600 },
            'h3': { fontSize: 24, fontSizeMobile: 20, fontWeight: '600', fontWeightMobile: '600', lineHeight: 1.4, lineHeightMobile: 1.4, maxWidth: 650, maxWidthMobile: 550 },
            'p': { fontSize: 16, fontSizeMobile: 14, fontWeight: '400', fontWeightMobile: '400', lineHeight: 1.6, lineHeightMobile: 1.6, maxWidth: 600, maxWidthMobile: 500 },
            'title': { fontSize: 48, fontSizeMobile: 32, fontWeight: '700', fontWeightMobile: '700', lineHeight: 1.2, lineHeightMobile: 1.2, maxWidth: 600, maxWidthMobile: 500 },
            'subtitle': { fontSize: 24, fontSizeMobile: 20, fontWeight: '400', fontWeightMobile: '400', lineHeight: 1.4, lineHeightMobile: 1.4, maxWidth: 550, maxWidthMobile: 450 },
            'description': { fontSize: 14, fontSizeMobile: 12, fontWeight: '400', fontWeightMobile: '400', lineHeight: 1.6, lineHeightMobile: 1.6, maxWidth: 500, maxWidthMobile: 400 },
            'additional': { fontSize: 18, fontSizeMobile: 16, fontWeight: '500', fontWeightMobile: '500', lineHeight: 1.6, lineHeightMobile: 1.6, maxWidth: 450, maxWidthMobile: 400 }
        };
        return defaults[elType] || defaults['p'];
    };
    
    const typeDefaults = getDefaultsByType(element.elementType);
    
    return {
        // Base Properties
        id: element.id,
        type: element.type || 'text',
        elementType: element.elementType || 'p',
        content: element.content || '',
        order: element.order ?? 0,
        visible: element.visible ?? true,
        
        // Font Styles (mit Defaults wenn undefined)
        fontSize: element.fontSize ?? typeDefaults.fontSize,
        fontSizeMobile: element.fontSizeMobile ?? typeDefaults.fontSizeMobile,
        fontWeight: element.fontWeight ?? typeDefaults.fontWeight,
        fontWeightMobile: element.fontWeightMobile ?? typeDefaults.fontWeightMobile,
        fontFamily: element.fontFamily || '',
        color: element.color || '#333333',
        
        // Typography
        lineHeight: element.lineHeight ?? typeDefaults.lineHeight,
        lineHeightMobile: element.lineHeightMobile ?? typeDefaults.lineHeightMobile,
        letterSpacing: element.letterSpacing ?? 0,
        letterSpacingMobile: element.letterSpacingMobile ?? 0,
        textAlign: element.textAlign || 'left',
        textAlignMobile: element.textAlignMobile || 'left',
        
        // Dimensions
        maxWidth: element.maxWidth ?? typeDefaults.maxWidth,
        maxWidthMobile: element.maxWidthMobile ?? typeDefaults.maxWidthMobile,
        
        // Margins Desktop
        marginTop: element.marginTop ?? 0,
        marginRight: element.marginRight ?? 0,
        marginBottom: element.marginBottom ?? 0,
        marginLeft: element.marginLeft ?? 0,
        
        // Margins Mobile
        marginTopMobile: element.marginTopMobile ?? 0,
        marginRightMobile: element.marginRightMobile ?? 0,
        marginBottomMobile: element.marginBottomMobile ?? 0,
        marginLeftMobile: element.marginLeftMobile ?? 0,
        
        // Other
        textEffect: element.textEffect || 'none',
        placeholder: element.placeholder || 'Enter text...'
    };
};

/**
 * Migriert alte HeroHeader-Struktur zu containerSections
 * Wird automatisch aufgerufen wenn keine containerSections vorhanden sind
 */
export const migrateToContainerSections = (data) => {
    // ✅ IMMER enrichen - auch wenn containerSections existieren!
    // Firebase löscht undefined Properties beim Speichern, also müssen wir sie mit Defaults füllen
    if (data.containerSections && data.containerSections.length > 0) {
        // console.log('🔄 Enriching existing containerSections with default properties...');
        
        const enrichedContainerSections = data.containerSections.map(container => ({
            ...container,
            contentElements: (container.contentElements || []).map(enrichElementWithDefaults)
        }));
        
        return {
            ...data,
            containerSections: enrichedContainerSections
        };
    }

    console.log('🔄 Migrating old HeroHeader to containerSections...');

    const containerSections = [];

    // Container 1: Text Content
    const textContainer = {
        id: 'container-text-1',
        order: 0,
        visible: true,
        type: 'text',
        
        // Container Styling - von alten container* Properties übernehmen
        containerPaddingTop: data.containerPaddingTop ?? 60,
        containerPaddingRight: data.containerPaddingRight ?? 40,
        containerPaddingBottom: data.containerPaddingBottom ?? 60,
        containerPaddingLeft: data.containerPaddingLeft ?? 40,
        containerPaddingTopMobile: data.containerPaddingTopMobile ?? data.containerPaddingTop ?? 40,
        containerPaddingRightMobile: data.containerPaddingRightMobile ?? data.containerPaddingRight ?? 20,
        containerPaddingBottomMobile: data.containerPaddingBottomMobile ?? data.containerPaddingBottom ?? 40,
        containerPaddingLeftMobile: data.containerPaddingLeftMobile ?? data.containerPaddingLeft ?? 20,
        
        containerMarginTop: data.containerMarginTop ?? 0,
        containerMarginRight: data.containerMarginRight ?? 0,
        containerMarginBottom: data.containerMarginBottom ?? 0,
        containerMarginLeft: data.containerMarginLeft ?? 0,
        containerMarginTopMobile: data.containerMarginTopMobile ?? 0,
        containerMarginRightMobile: data.containerMarginRightMobile ?? 0,
        containerMarginBottomMobile: data.containerMarginBottomMobile ?? 0,
        containerMarginLeftMobile: data.containerMarginLeftMobile ?? 0,
        
        // Background (Desktop)
        containerBackgroundColor: data.leftBackgroundColor || 'transparent',
        containerBackgroundColorOpacity: data.containerBackgroundColorOpacity ?? 100,
        containerBackdropFilterBlur: data.containerBackdropFilterBlur ?? 0,
        containerBackgroundGradientEnabled: data.containerBackgroundGradientEnabled ?? false,
        containerBackgroundGradientColor1: data.containerBackgroundGradientColor1 || '#667eea',
        containerBackgroundGradientColor2: data.containerBackgroundGradientColor2 || '#764ba2',
        containerBackgroundImage: data.containerBackgroundImage || '',
        containerBackgroundImageSize: data.containerBackgroundImageSize || 'cover',
        containerBackgroundImagePosition: data.containerBackgroundImagePosition || 'center',
        containerBackgroundImageOpacity: data.containerBackgroundImageOpacity ?? 100,
        containerBackgroundImageScale: data.containerBackgroundImageScale ?? 100,
        
        // ✅ Background (Mobile)
        containerBackgroundColorMobile: data.containerBackgroundColorMobile ?? null,
        containerBackgroundColorOpacityMobile: data.containerBackgroundColorOpacityMobile ?? 100,
        containerBackdropFilterBlurMobile: data.containerBackdropFilterBlurMobile ?? 0,
        containerBackgroundGradientEnabledMobile: data.containerBackgroundGradientEnabledMobile ?? null,
        containerBackgroundGradientColor1Mobile: data.containerBackgroundGradientColor1Mobile ?? null,
        containerBackgroundGradientColor2Mobile: data.containerBackgroundGradientColor2Mobile ?? null,
        containerBackgroundImageMobile: data.containerBackgroundImageMobile ?? null,
        containerBackgroundImageSizeMobile: data.containerBackgroundImageSizeMobile ?? null,
        containerBackgroundImageScaleMobile: data.containerBackgroundImageScaleMobile ?? null,
        containerBackgroundImagePositionMobile: data.containerBackgroundImagePositionMobile ?? null,
        containerBackgroundImageOpacityMobile: data.containerBackgroundImageOpacityMobile ?? null,
        
        containerBorderEnabled: data.containerBorderEnabled ?? false,
        containerBorderWidth: data.containerBorderWidth || 1,
        containerBorderColor: data.containerBorderColor || '#cccccc',
        containerBorderRadius: data.containerBorderRadius ?? 0,
        containerBorderRadiusMobile: data.containerBorderRadiusMobile ?? 0,
        containerShadowEnabled: data.containerShadowEnabled ?? false,
        containerShadowIntensity: data.containerShadowIntensity || 15,
        containerShadowDistance: data.containerShadowDistance || 8,
        containerShadowOpacity: data.containerShadowOpacity || 25,
        
        containerContentAlignmentVertical: data.containerContentAlignmentVertical || 'center',
        containerContentAlignmentHorizontal: data.containerContentAlignmentHorizontal || 'flex-start',
        
        containerWidth: data.containerWidth || 'auto',
        containerHeight: data.containerHeight || 'auto',
        containerWidthMobile: data.containerWidthMobile || 'auto',
        containerHeightMobile: data.containerHeightMobile || 'auto',
        
        // Content Elements - ✅ ENRICHED mit Default-Properties!
        contentElements: (data.contentElements || []).map(enrichElementWithDefaults)
    };

    containerSections.push(textContainer);

    // Container 2: Image Container (nur wenn Hero Image aktiv ist)
    if (data.showHeroImage || data.heroImage) {
        const imageContainer = {
            id: 'container-image-1',
            order: 1,
            visible: true,
            type: 'image',
            
            // Container Styling - von alten imageContainer* Properties übernehmen
            containerPaddingTop: data.imageContainerPaddingTop ?? 0,
            containerPaddingRight: data.imageContainerPaddingRight ?? 0,
            containerPaddingBottom: data.imageContainerPaddingBottom ?? 0,
            containerPaddingLeft: data.imageContainerPaddingLeft ?? 0,
            containerPaddingTopMobile: data.imageContainerPaddingTopMobile ?? 0,
            containerPaddingRightMobile: data.imageContainerPaddingRightMobile ?? 0,
            containerPaddingBottomMobile: data.imageContainerPaddingBottomMobile ?? 0,
            containerPaddingLeftMobile: data.imageContainerPaddingLeftMobile ?? 0,
            
            containerMarginTop: data.imageContainerMarginTop ?? 0,
            containerMarginRight: data.imageContainerMarginRight ?? 0,
            containerMarginBottom: data.imageContainerMarginBottom ?? 0,
            containerMarginLeft: data.imageContainerMarginLeft ?? 0,
            containerMarginTopMobile: data.imageContainerMarginTopMobile ?? 0,
            containerMarginRightMobile: data.imageContainerMarginRightMobile ?? 0,
            containerMarginBottomMobile: data.imageContainerMarginBottomMobile ?? 0,
            containerMarginLeftMobile: data.imageContainerMarginLeftMobile ?? 0,
            
            containerBackgroundColor: data.imageContainerBackgroundColor || 'transparent',
            containerBackgroundGradientEnabled: data.imageContainerBackgroundGradientEnabled ?? false,
            containerBackgroundGradientColor1: data.imageContainerBackgroundGradientColor1 || '#667eea',
            containerBackgroundGradientColor2: data.imageContainerBackgroundGradientColor2 || '#764ba2',
            containerBackgroundImage: data.heroImage || data.imageContainerBackgroundImage || '',
            containerBackgroundImageSize: data.imageContainerBackgroundImageSize || 'cover',
            containerBackgroundImagePosition: data.imageContainerBackgroundImagePosition || 'center',
            containerBackgroundImageOpacity: data.imageContainerBackgroundImageOpacity ?? 100,
            
            containerBorderEnabled: data.imageContainerBorderEnabled ?? false,
            containerBorderWidth: data.imageContainerBorderWidth || 1,
            containerBorderColor: data.imageContainerBorderColor || '#cccccc',
            containerBorderRadius: data.imageContainerBorderRadius ?? 0,
            containerBorderRadiusMobile: data.imageContainerBorderRadiusMobile ?? 0,
            containerShadowEnabled: data.imageContainerShadowEnabled ?? false,
            containerShadowIntensity: data.imageContainerShadowIntensity || 15,
            containerShadowDistance: data.imageContainerShadowDistance || 8,
            containerShadowOpacity: data.imageContainerShadowOpacity || 25,
            
            containerContentAlignmentVertical: data.imageContainerContentAlignmentVertical || 'center',
            containerContentAlignmentHorizontal: data.imageContainerContentAlignmentHorizontal || 'center',
            
            containerWidth: data.imageContainerWidth || 'auto',
            containerHeight: data.imageContainerHeight || 'auto',
            containerWidthMobile: data.imageContainerWidthMobile || 'auto',
            containerHeightMobile: data.imageContainerHeightMobile || 'auto',
            
            // Empty content elements (Image ist Background) - ✅ ENRICHED!
            contentElements: (data.imageContainerElements || []).map(enrichElementWithDefaults)
        };

        containerSections.push(imageContainer);
    }

    // Grid Layout basierend auf childDistribution
    let gridLayout = 'grid-cols-2-equal';
    if (data.childDistribution) {
        const distribution = data.childDistribution;
        if (distribution === '100') gridLayout = 'grid-cols-1'; // ✅ Single child - 100% width
        else if (distribution === '50-50') gridLayout = 'grid-cols-2-equal';
        else if (distribution === '60-40') gridLayout = 'grid-cols-2-60-40';
        else if (distribution === '40-60') gridLayout = 'grid-cols-2-40-60';
        else if (distribution === '70-30') gridLayout = 'grid-cols-2-70-30';
        else if (distribution === '30-70') gridLayout = 'grid-cols-2-30-70';
    }

    return {
        ...data,
        containerSections,
        gridLayout
    };
};

/**
 * Fügt einen neuen Container hinzu
 */
export const addContainer = (data, containerType = 'text') => {
    console.log('🔥 [addContainer] Called with:', { containerType, currentSections: data.containerSections });
    
    const currentSections = data.containerSections || [];
    const newOrder = currentSections.length;
    
    console.log('🔥 [addContainer] Current sections count:', currentSections.length);
    console.log('🔥 [addContainer] New order will be:', newOrder);

    const defaults = {
        text: {
            containerBackgroundColor: 'transparent',
            containerContentAlignmentHorizontal: 'flex-start',
            contentElements: []
        },
        image: {
            containerBackgroundColor: 'transparent',
            containerBackgroundImage: '',
            containerContentAlignmentHorizontal: 'center',
            contentElements: []
        },
        mixed: {
            containerBackgroundColor: 'transparent',
            containerBackgroundImage: '',
            containerContentAlignmentHorizontal: 'center',
            contentElements: []
        },
        empty: {
            containerBackgroundColor: 'transparent',
            containerContentAlignmentHorizontal: 'center',
            contentElements: []
        }
    };

    const newContainer = {
        id: `container-${containerType}-${Date.now()}`,
        order: newOrder,
        visible: true,
        type: containerType,
        
        // Default Container Styling
        containerPaddingTop: 60,
        containerPaddingRight: 40,
        containerPaddingBottom: 60,
        containerPaddingLeft: 40,
        containerPaddingTopMobile: 40,
        containerPaddingRightMobile: 20,
        containerPaddingBottomMobile: 40,
        containerPaddingLeftMobile: 20,
        
        containerMarginTop: 0,
        containerMarginRight: 0,
        containerMarginBottom: 0,
        containerMarginLeft: 0,
        containerMarginTopMobile: 0,
        containerMarginRightMobile: 0,
        containerMarginBottomMobile: 0,
        containerMarginLeftMobile: 0,
        
        // Background (Desktop)
        containerBackgroundColorOpacity: 100,
        containerBackdropFilterBlur: 0,
        containerBackgroundGradientEnabled: false,
        containerBackgroundGradientColor1: '#667eea',
        containerBackgroundGradientColor2: '#764ba2',
        containerBackgroundImageSize: 'cover',
        containerBackgroundImagePosition: 'center',
        containerBackgroundImageOpacity: 100,
        containerBackgroundImageScale: 100,
        
        // ✅ Background (Mobile)
        containerBackgroundColorMobile: null,
        containerBackgroundColorOpacityMobile: 100,
        containerBackdropFilterBlurMobile: 0,
        containerBackgroundGradientEnabledMobile: null,
        containerBackgroundGradientColor1Mobile: null,
        containerBackgroundGradientColor2Mobile: null,
        containerBackgroundImageMobile: null,
        containerBackgroundImageSizeMobile: null,
        containerBackgroundImageScaleMobile: null,
        containerBackgroundImagePositionMobile: null,
        containerBackgroundImageOpacityMobile: null,
        
        containerBorderEnabled: false,
        containerBorderWidth: 1,
        containerBorderColor: '#cccccc',
        containerBorderRadius: 0,
        containerBorderRadiusMobile: 0,
        containerShadowEnabled: false,
        containerShadowIntensity: 15,
        containerShadowDistance: 8,
        containerShadowOpacity: 25,
        
        containerContentAlignmentVertical: 'center',
        
        containerWidth: 'auto',
        containerHeight: 'auto',
        containerWidthMobile: 'auto',
        containerHeightMobile: 'auto',
        
        // Type-specific defaults
        ...defaults[containerType]
    };

    const newSections = [...currentSections, newContainer];
    
    console.log('🔥 [addContainer] New container created:', newContainer);
    console.log('🔥 [addContainer] New sections array:', newSections);
    console.log('🔥 [addContainer] New sections count:', newSections.length);

    // Auto-update Grid Layout
    const gridLayout = getGridLayoutForContainerCount(newSections.length);
    
    console.log('🔥 [addContainer] Grid layout:', gridLayout);

    const result = {
        ...data,
        containerSections: newSections,
        gridLayout
    };
    
    console.log('🔥 [addContainer] Returning data:', result);
    
    return result;
};

/**
 * Löscht einen Container
 */
export const deleteContainer = (data, containerId) => {
    const currentSections = data.containerSections || [];
    const newSections = currentSections.filter(c => c.id !== containerId);

    // Auto-update Grid Layout
    const gridLayout = getGridLayoutForContainerCount(newSections.length);

    return {
        ...data,
        containerSections: newSections,
        gridLayout
    };
};

/**
 * Updatet einen Container
 */
export const updateContainer = (data, updatedContainer) => {
    const currentSections = data.containerSections || [];
    const newSections = currentSections.map(c => 
        c.id === updatedContainer.id ? updatedContainer : c
    );

    return {
        ...data,
        containerSections: newSections
    };
};

/**
 * Gibt passenden Grid Layout String zurück basierend auf Container-Anzahl
 * ✅ Unterstützt jetzt bis zu 10 Container
 */
export const getGridLayoutForContainerCount = (count) => {
    if (count === 1) return 'grid-cols-1';
    if (count === 2) return 'grid-cols-2-equal';
    if (count === 3) return 'grid-cols-3-equal';
    if (count === 4) return 'grid-cols-4-equal';
    if (count === 5) return 'grid-cols-5-equal';
    if (count === 6) return 'grid-cols-6-equal';
    if (count === 7) return 'grid-cols-7-equal';
    if (count === 8) return 'grid-cols-8-equal';
    if (count === 9) return 'grid-cols-9-equal';
    if (count === 10) return 'grid-cols-10-equal';
    
    // Bei mehr als 10: Stacking (vertikal)
    return 'grid-cols-1';
};

/**
 * Konvertiert Grid Layout String zu CSS Flex-Basis Werten
 * ✅ Garantiert nie über 100% Gesamtbreite
 */
export const getGridFlexBasis = (gridLayout, containerIndex) => {
    const layouts = {
        // ✅ Single container layouts (centered with max-width)
        'grid-cols-1': ['100%'],
        'grid-cols-1-100': ['100%'],
        'grid-cols-1-90': ['90%'],
        'grid-cols-1-80': ['80%'],
        'grid-cols-1-70': ['70%'],
        'grid-cols-1-60': ['60%'],
        'grid-cols-1-50': ['50%'],
        // Two column layouts
        'grid-cols-2-equal': ['50%', '50%'],
        'grid-cols-2-60-40': ['60%', '40%'],
        'grid-cols-2-40-60': ['40%', '60%'],
        'grid-cols-2-70-30': ['70%', '30%'],
        'grid-cols-2-30-70': ['30%', '70%'],
        // Three column layouts
        'grid-cols-3-equal': ['33.333%', '33.333%', '33.334%'],
        'grid-cols-3-50-25-25': ['50%', '25%', '25%'],
        'grid-cols-3-25-50-25': ['25%', '50%', '25%'],
        'grid-cols-3-25-25-50': ['25%', '25%', '50%'],
        // Four column layouts
        'grid-cols-4-equal': ['25%', '25%', '25%', '25%'],
        'grid-cols-4-40-20-20-20': ['40%', '20%', '20%', '20%'],
        // Five column layout
        'grid-cols-5-equal': ['20%', '20%', '20%', '20%', '20%'],
        // ✅ NEW: 6-10 Columns Support
        'grid-cols-6-equal': ['16.666%', '16.666%', '16.666%', '16.666%', '16.666%', '16.67%'],
        'grid-cols-7-equal': ['14.285%', '14.285%', '14.285%', '14.285%', '14.285%', '14.285%', '14.29%'],
        'grid-cols-8-equal': ['12.5%', '12.5%', '12.5%', '12.5%', '12.5%', '12.5%', '12.5%', '12.5%'],
        'grid-cols-9-equal': ['11.111%', '11.111%', '11.111%', '11.111%', '11.111%', '11.111%', '11.111%', '11.111%', '11.112%'],
        'grid-cols-10-equal': ['10%', '10%', '10%', '10%', '10%', '10%', '10%', '10%', '10%', '10%']
    };

    const flexBasisArray = layouts[gridLayout] || ['50%', '50%'];
    const basis = flexBasisArray[containerIndex] || '1';
    
    // ✅ flex: 0 0 X% verhindert grow/shrink über die Basis hinaus
    return `0 0 ${basis}`;
};

